import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Trash2, ArrowLeft, Sparkles, PenLine, Edit2, FileSpreadsheet, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import Navbar from '../components/Navbar';
import { isEmployee, isPlatformAdmin } from '../lib/roles';
import { postBook } from '../lib/postBook';

const READING_AGES = ['4-12', '13-21', '22-30', '30+'];

const matchOption = (value, options) => {
  if (!value) return '';
  const lower = String(value).toLowerCase().trim();
  const exact = options.find((o) => o.toLowerCase() === lower);
  if (exact) return exact;
  const partial = options.find((o) => o.toLowerCase().includes(lower) || lower.includes(o.toLowerCase()));
  return partial || '';
};

const toIntOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

const bookCoverUrl = (book) => {
  const raw = book?.cover_url || book?.cover_image;
  if (raw) {
    return String(raw).replace('http://', 'https://').replace('zoom=1', 'zoom=2');
  }
  const isbn = String(book?.isbn_13 || book?.isbn_10 || book?.isbn || '').replace(/\D/g, '');
  if (isbn.length === 10 || isbn.length === 13) {
    return `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg`;
  }
  return '';
};

const AdminDashboard = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myBooks, setMyBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchStep, setSearchStep] = useState(1); // 1: search, 2: select, 3: new book, 4: existing copy
  const [searchMessage, setSearchMessage] = useState('');
  const [existingPick, setExistingPick] = useState(null);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyForm, setCopyForm] = useState({
    useProfileAddress: true,
    address: '',
    city: '',
    pincode: '',
    mrp: '',
    condition: 'good',
  });
  const [prosumerOwners, setProsumerOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [ownerOrders, setOwnerOrders] = useState([]);
  const [damageOrder, setDamageOrder] = useState(null);
  const [damageNotes, setDamageNotes] = useState('');
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [damageSubmitting, setDamageSubmitting] = useState(false);
  const [declineOrderId, setDeclineOrderId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [refundingId, setRefundingId] = useState(null);
  const [bookStats, setBookStats] = useState({ total: 0, available: 0 });
  
  // Add Book Form
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState({
    title: '',
    author: '',
    category: '',
    subcategory: '',
    price: '',
    stock: 1,
    pincode: user?.pincode || '',
    description: '',
    cover_image: '',
    publisher: '',
    pages: '',
    language: 'English',
    weight_gms: '',
    size_cms: '',
    reading_age: '',
    packer: user?.name || ''
  });
  const [searchTitle, setSearchTitle] = useState('');

  const canAssignOwner = isEmployee(user?.role) || isPlatformAdmin(user?.role);

  // Get selected category's subcategories
  const selectedCategory = categories.find(cat => cat.name === bookForm.category);
  const hasSubcategories = selectedCategory?.subcategories?.length > 0;

  useEffect(() => {
    fetchCategories();
    fetchMyBooks();
    fetchOwnerOrders();
  }, []);

  useEffect(() => {
    if (searchParams.get('add') === '1') {
      setShowAddDialog(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!canAssignOwner || !token) return;
    const loadOwners = async () => {
      try {
        const res = await axios.get(`${API}/admin/prosumers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProsumerOwners(res.data || []);
      } catch (e) {
        setProsumerOwners([]);
      }
    };
    loadOwners();
  }, [canAssignOwner, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  };

  const fetchMyBooks = async () => {
    setLoading(true);
    try {
      // Legacy books — fetch ONLY this owner's (server-side filter), all of them.
      const legacyResp = await axios.get(`${API}/books`, {
        params: { owner_id: user.id, limit: 500, page: 1 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const booksData = legacyResp.data.books || legacyResp.data;
      const allBooks = Array.isArray(booksData) ? booksData : [];
      const legacyBooks = allBooks.map(b => ({ ...b, _source: 'legacy' }));

      // Two-layer listings (the new Add Book / Catalog flow)
      let twoLayerBooks = [];
      try {
        const twoLayerResp = await axios.get(`${API}/my-listings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        twoLayerBooks = (twoLayerResp.data || []).map(c => ({
          // Map two-layer copy → legacy-shape so the existing UI just works
          id: c.id,
          book_id: c.book_id,
          title: c.title,
          author: c.author,
          cover_image: c.cover_image,
          description: c.description,
          isbn: c.isbn,
          pincode: c.pincode,
          city: c.city,
          condition: c.condition,
          price: c.daily_rate, // shown in INR/day on the new model
          stock: 1,
          status: c.status,
          is_available: c.status === 'available',
          owner_id: user.id,
          mirrored_from_legacy_id: c.mirrored_from_legacy_id,
          _source: 'two_layer',
        }));
      } catch (e) {
        // Two-layer endpoint optional; ignore silently if not available
      }

      // De-dupe: don't show a two-layer mirror entry when the legacy row is already shown
      // (legacy POST /api/books now mirrors into copies; we'd double-count otherwise).
      const legacyIds = new Set(legacyBooks.map(b => b.id));
      const twoLayerOnly = twoLayerBooks.filter(c => !legacyIds.has(c.id) && !(c.mirrored_from_legacy_id && legacyIds.has(c.mirrored_from_legacy_id)));

      const merged = [...twoLayerOnly, ...legacyBooks];
      setMyBooks(merged);

      // MBR-096: count from legacy inventory only (mirrors duplicate the same physical copies)
      const statsSource = legacyBooks.length > 0 ? legacyBooks : twoLayerOnly;
      setBookStats({
        total: statsSource.reduce((sum, b) => sum + (Number(b.stock) || Number(b.quantity) || 1), 0),
        available: statsSource
          .filter(b => b.is_available || b.status === 'available')
          .reduce((sum, b) => sum + (Number(b.stock) || Number(b.quantity) || 1), 0),
      });
    } catch (error) {
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerOrders = async () => {
    try {
      const res = await axios.get(`${API}/owner/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwnerOrders(res.data || []);
    } catch {
      setOwnerOrders([]);
    }
  };

  const handleDamagePhotoChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    Promise.all(files.map((file) => new Promise((resolve, reject) => {
      if (file.size > 2 * 1024 * 1024) {
        reject(new Error(`${file.name} is over 2MB`));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }))).then((photos) => {
      setDamagePhotos(photos);
    }).catch((err) => toast.error(err.message || 'Failed to read photos'));
  };

  const submitDamageReport = async () => {
    if (!damageOrder) return;
    if ((damageNotes || '').trim().length < 10) {
      toast.error('Please describe the damage (at least 10 characters)');
      return;
    }
    setDamageSubmitting(true);
    try {
      await axios.post(
        `${API}/orders/${damageOrder.id}/damage-report`,
        { notes: damageNotes.trim(), photos: damagePhotos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Damage report submitted for review');
      setDamageOrder(null);
      setDamageNotes('');
      setDamagePhotos([]);
      fetchOwnerOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit damage report');
    } finally {
      setDamageSubmitting(false);
    }
  };

  const pendingApprovalOrders = ownerOrders.filter(
    (o) => o.status === 'paid_pending_lender_approval' || o.lender_approval_status === 'pending'
  );

  const handleLenderApprove = async (orderId) => {
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/lender/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Order approved');
      fetchOwnerOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve order');
    }
  };

  const handleLenderReject = (orderId) => {
    setDeclineOrderId(orderId);
    setDeclineReason('');
  };

  const submitLenderReject = async (alsoRefund) => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Please type a reason for declining');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/orders/${declineOrderId}/lender/reject`,
        { reason, refund: alsoRefund },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Order declined');
      setDeclineOrderId(null);
      setDeclineReason('');
      fetchOwnerOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline order');
    }
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm('Refund this rental payment to the borrower? It reaches their original payment method in 3–4 business days.')) return;
    setRefundingId(orderId);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Refund initiated');
      fetchOwnerOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundingId(null);
    }
  };

  const handleSearchBooks = async () => {
    if (!searchTitle) {
      toast.error('Please enter book title');
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(
        `${API}/books/ai-search`,
        { title: searchTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSearchResults(response.data.books || []);
      setSearchMessage(response.data.message || '');
      setSearchStep(2);
      const count = response.data.books?.length || 0;
      if (response.data.source_priority === 'internal') {
        toast.success(`Found ${count} book(s) already on MyBook.Rent`);
      } else {
        toast.success(`Found ${count} books`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search books');
    } finally {
      setAiLoading(false);
    }
  };

  const applyBookFields = (src, prev) => {
    const catNames = categories.map((c) => c.name);
    const next = { ...prev };
    const setIfEmpty = (key, val) => {
      if ((next[key] === '' || next[key] == null) && val != null && val !== '') {
        next[key] = String(val);
      }
    };
    if (src.title) next.title = src.title;
    if (src.author) next.author = src.author;
    setIfEmpty('description', src.description);
    setIfEmpty('publisher', src.publisher);
    setIfEmpty('pages', src.pages);
    setIfEmpty('language', src.language);
    setIfEmpty('cover_image', bookCoverUrl(src) || src.cover_url || src.cover_image);
    setIfEmpty('price', src.price);
    const weight = src.weight_gms ?? src.weight;
    if (weight != null && weight !== '') {
      setIfEmpty('weight_gms', String(weight).replace(/[^\d.]/g, ''));
    }
    setIfEmpty('size_cms', src.size_cms || src.size);
    const matchedCat = matchOption(src.category, catNames);
    if (matchedCat) setIfEmpty('category', matchedCat);
    const matchedAge = matchOption(src.reading_age || src.age_category, READING_AGES);
    if (matchedAge) setIfEmpty('reading_age', matchedAge);
    return next;
  };

  const dailyRateFromMrp = (mrp) => {
    const n = parseFloat(mrp);
    if (!n || n <= 0) return 0;
    return Math.round((n * 0.30) / 7 * 100) / 100;
  };

  const openCopyDetails = (book) => {
    const hasProfile = Boolean(user?.city && user?.pincode);
    setExistingPick(book);
    setCopyForm({
      useProfileAddress: hasProfile,
      address: user?.address || '',
      city: user?.city || '',
      pincode: user?.pincode || '',
      mrp: book.price ? String(book.price) : '',
      condition: 'good',
    });
    setSearchStep(4);
  };

  const handleSelectBook = async (selectedBook) => {
    if (selectedBook.source === 'internal') {
      openCopyDetails(selectedBook);
      return;
    }
    setBookForm((prev) => applyBookFields(selectedBook, prev));
    setSearchStep(3);
    const filled = [
      selectedBook.category,
      selectedBook.price,
      selectedBook.pages,
      selectedBook.weight || selectedBook.weight_gms,
      selectedBook.reading_age,
    ].filter((v) => v != null && v !== '').length;
    if (filled >= 3) {
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/books/enrich`,
        { title: selectedBook.title, author: selectedBook.author, fast: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookForm((prev) => applyBookFields({
        ...selectedBook,
        ...data,
        cover_url: data.cover_image || selectedBook.cover_url,
        weight: data.weight,
        size: data.size,
      }, prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not auto-fill extra details. Please enter them manually.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchStep(1);
    setSearchResults([]);
    setSearchTitle('');
    setSearchMessage('');
    setSelectedOwnerId('');
    setExistingPick(null);
    setCopyForm({
      useProfileAddress: Boolean(user?.city && user?.pincode),
      address: user?.address || '',
      city: user?.city || '',
      pincode: user?.pincode || '',
      mrp: '',
      condition: 'good',
    });
    setBookForm({
      title: '',
      author: '',
      category: '',
      subcategory: '',
      price: '',
      stock: 1,
      pincode: user?.pincode || '',
      description: '',
      cover_image: '',
      publisher: '',
      pages: '',
      language: 'English',
      weight_gms: '',
      size_cms: '',
      reading_age: '',
      packer: user?.name || ''
    });
  };

  const handleAddBook = async (e) => {
    e.preventDefault();

    if (!bookForm.title || !bookForm.author || !bookForm.category || !bookForm.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // If Language category is selected, subcategory is required
    if (bookForm.category === 'Language' && !bookForm.subcategory) {
      toast.error('Please select a language');
      return;
    }

    try {
      // For Language category, append subcategory to category name
      const categoryValue = bookForm.category === 'Language' && bookForm.subcategory 
        ? `Language - ${bookForm.subcategory}` 
        : bookForm.category;
      
      // Calculate rental price as 30% of MRP
      const price = parseFloat(bookForm.price);
      const rentalPrice = Math.round(price * 0.30);
      
      await postBook(API, token, {
          title: bookForm.title,
          author: bookForm.author,
          category: categoryValue,
          price,
          rental_price_per_week: rentalPrice,
          stock: parseInt(bookForm.stock, 10) || 1,
          pincode: bookForm.pincode,
          description: bookForm.description || null,
          cover_image: bookForm.cover_image || null,
          publisher: bookForm.publisher || null,
          pages: toIntOrNull(bookForm.pages),
          language: bookForm.language || 'English',
          weight_gms: toIntOrNull(bookForm.weight_gms),
          size_cms: bookForm.size_cms || null,
          reading_age: bookForm.reading_age || null,
          age_category: bookForm.reading_age || null,
          ...(canAssignOwner && selectedOwnerId ? { owner_id: selectedOwnerId } : {})
        });

      toast.success('Book added successfully!');
      setShowAddDialog(false);
      setBookForm({
        title: '',
        author: '',
        category: '',
        subcategory: '',
        price: '',
        stock: 1,
        pincode: user?.pincode || '',
        description: '',
        cover_image: '',
        publisher: '',
        pages: '',
        language: 'English',
        weight_gms: '',
        size_cms: '',
        reading_age: '',
        packer: user?.name || ''
      });
      fetchMyBooks();
    } catch (error) {
      if (error?.cancelled) return;
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.loc?.join('.') || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : detail?.message) || 'Failed to add book';
      toast.error(typeof msg === 'string' ? msg : 'Failed to add book');
    }
  };

  const handleAddExistingCopy = async (e) => {
    e.preventDefault();
    if (!existingPick) return;
    const city = copyForm.useProfileAddress ? (user?.city || '') : copyForm.city.trim();
    const pincode = copyForm.useProfileAddress ? (user?.pincode || '') : copyForm.pincode.trim();
    if (!city || !pincode) {
      toast.error('City and pincode are required');
      return;
    }
    const mrp = parseFloat(copyForm.mrp);
    if (!mrp || mrp <= 0) {
      toast.error('Enter your MRP');
      return;
    }
    setCopySubmitting(true);
    try {
      const weekly = Math.round(mrp * 0.30);
      await postBook(API, token, {
          title: existingPick.title,
          author: existingPick.author,
          category: existingPick.category || 'Others',
          price: mrp,
          rental_price_per_week: weekly,
          stock: 1,
          pincode,
          pickup_city: city,
          condition: copyForm.condition,
          cover_image: bookCoverUrl(existingPick) || null,
          description: existingPick.description || null,
          publisher: existingPick.publisher || null,
          pages: toIntOrNull(existingPick.pages),
          language: existingPick.language || 'English',
          weight_gms: toIntOrNull(existingPick.weight_gms || existingPick.weight),
          size_cms: existingPick.size_cms || existingPick.size || null,
          reading_age: existingPick.reading_age || null,
          ...(canAssignOwner && selectedOwnerId ? { owner_id: selectedOwnerId } : {})
        });
      toast.success('Your copy was added to the existing book record.');
      setShowAddDialog(false);
      handleResetSearch();
      fetchMyBooks();
    } catch (error) {
      if (error?.cancelled) return;
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to add your copy'));
    } finally {
      setCopySubmitting(false);
    }
  };

  const handleDeleteBook = async (book) => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      if (book._source === 'two_layer') {
        await axios.delete(`${API}/copies/${book.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/books/${book.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Book deleted successfully');
      fetchMyBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  const normTitle = (t) => (t || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const getEditDuplicateMatch = () => {
    if (!editingBook || !bookForm.title || !bookForm.author) return null;
    const key = `${normTitle(bookForm.title)}|${normTitle(bookForm.author)}`;
    return myBooks.find((b) => {
      if (b.id === editingBook.id) return false;
      return `${normTitle(b.title)}|${normTitle(b.author)}` === key;
    }) || null;
  };

  const editDuplicateMatch = getEditDuplicateMatch();

  const handleEditBook = (book) => {
    setEditingBook(book);
    setBookForm({
      title: book.title || '',
      author: book.author || '',
      category: book.category?.includes(' - ') ? book.category.split(' - ')[0] : book.category || '',
      subcategory: book.category?.includes(' - ') ? book.category.split(' - ')[1] : '',
      price: book.price?.toString() || '',
      stock: book.stock || 1,
      pincode: book.pincode || '',
      description: book.description || '',
      cover_image: book.cover_image || '',
      publisher: book.publisher || '',
      pages: book.pages?.toString() || '',
      language: book.language || 'English',
      weight_gms: book.weight_gms?.toString() || '',
      size_cms: book.size_cms || '',
      reading_age: book.reading_age || '',
      packer: book.packer || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateBook = async (e) => {
    e.preventDefault();

    if (!bookForm.title || !bookForm.author || !bookForm.category || !bookForm.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const categoryValue = bookForm.category === 'Language' && bookForm.subcategory 
        ? `Language - ${bookForm.subcategory}` 
        : bookForm.category;
      
      const price = parseFloat(bookForm.price);
      const rentalPrice = Math.round(price * 0.30);
      
      await axios.put(
        `${API}/books/${editingBook.id}`,
        {
          title: bookForm.title,
          author: bookForm.author,
          category: categoryValue,
          price,
          rental_price_per_week: rentalPrice,
          stock: parseInt(bookForm.stock, 10) || 1,
          pincode: bookForm.pincode,
          description: bookForm.description || null,
          cover_image: bookForm.cover_image || null,
          publisher: bookForm.publisher || null,
          pages: toIntOrNull(bookForm.pages),
          language: bookForm.language || 'English',
          weight_gms: toIntOrNull(bookForm.weight_gms),
          size_cms: bookForm.size_cms || null,
          reading_age: bookForm.reading_age || null,
          age_category: bookForm.reading_age || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Book updated successfully!');
      setShowEditDialog(false);
      setEditingBook(null);
      setBookForm({
        title: '',
        author: '',
        category: '',
        subcategory: '',
        price: '',
        stock: 1,
        pincode: user?.pincode || '',
        description: '',
        cover_image: '',
        publisher: '',
        pages: '',
        language: 'English',
        weight_gms: '',
        size_cms: '',
        reading_age: '',
        packer: user?.name || ''
      });
      fetchMyBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update book');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="rounded-3xl shadow-xl p-8 mb-8" style={{backgroundColor: 'var(--primary)', color: 'white'}}>
          <h2 className="text-3xl font-bold mb-2">Welcome, {user?.name}!</h2>
          <p className="opacity-90">Manage your book collection and track rentals</p>
          <div className="mt-6 flex space-x-6">
            <div>
              <p className="opacity-90 text-sm">Total Books</p>
              <p className="text-3xl font-bold" data-testid="total-books">{bookStats.total}</p>
            </div>
            <div>
              <p className="opacity-90 text-sm">Available</p>
              <p className="text-3xl font-bold" data-testid="available-books">{bookStats.available}</p>
            </div>
          </div>
        </div>

        {/* MBR-138: Lender approval for paid rental requests */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8" data-testid="lender-approval-section">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Rental Requests — Awaiting Your Approval
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Borrowers have paid for these rentals. Approve to confirm the booking and release the pickup code.
          </p>
          {pendingApprovalOrders.length === 0 ? (
            <p className="text-sm text-gray-400 border rounded-lg p-4 bg-gray-50">
              No pending approval requests.
            </p>
          ) : (
            <div className="space-y-3">
              {pendingApprovalOrders.map((order) => (
                <div key={order.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-lg p-4 border-blue-200 bg-blue-50/40">
                  <div>
                    <p className="font-medium">Order #{order.id?.slice(0, 8)}</p>
                    <p className="text-sm text-gray-700">
                      {(order.owned_items || order.items || []).map((i) => i.book_title || i.title).filter(Boolean).join(', ') || 'Book(s)'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {order.customer_name || 'Borrower'} · ₹{Math.round(order.final_amount || 0)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-700 hover:bg-green-800"
                      onClick={() => handleLenderApprove(order.id)}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700"
                      onClick={() => handleLenderReject(order.id)}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-8" data-testid="lender-refund-section">
          <h3 className="text-xl font-bold mb-2">Refund cancelled or declined orders</h3>
          <p className="text-sm text-gray-500 mb-4">
            One click sends the rental payment back through Razorpay. The borrower sees it on their original payment method in 3–4 business days.
          </p>
          {ownerOrders.filter((o) =>
            ['cancelled', 'rejected_by_lender'].includes((o.status || '').toLowerCase())
            && o.refund_status !== 'processed'
            && o.payment_status === 'completed'
          ).length === 0 ? (
            <p className="text-sm text-gray-400 border rounded-lg p-4 bg-gray-50">No refunds waiting.</p>
          ) : (
            <div className="space-y-3">
              {ownerOrders.filter((o) =>
                ['cancelled', 'rejected_by_lender'].includes((o.status || '').toLowerCase())
                && o.refund_status !== 'processed'
                && o.payment_status === 'completed'
              ).map((order) => (
                <div key={order.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-lg p-4">
                  <div>
                    <p className="font-medium">Order #{order.id?.slice(0, 8)} · {(order.status || '').replace(/_/g, ' ')}</p>
                    <p className="text-sm text-gray-600">₹{Math.round(order.final_amount || 0)}{order.lender_rejection_reason ? ` · ${order.lender_rejection_reason}` : ''}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={refundingId === order.id}
                    onClick={() => handleRefund(order.id)}
                    style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                  >
                    {refundingId === order.id ? 'Refunding…' : 'Refund payment'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={Boolean(declineOrderId)} onOpenChange={(open) => { if (!open) setDeclineOrderId(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Decline this rental</DialogTitle>
            </DialogHeader>
            <Label htmlFor="decline-reason">Reason</Label>
            <Textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Tell the borrower why you are declining"
              className="mt-2"
            />
            <p className="text-sm text-gray-500 mt-1">Razorpay sends the rental payment back. You do not transfer it yourself. It shows on the borrower’s original payment method in 3–4 business days.</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => submitLenderReject(true)}>
                Decline and refund
              </Button>
              <Button variant="outline" onClick={() => submitLenderReject(false)}>
                Decline without refund
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* MBR-124: Lender damage report — always visible */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8" data-testid="lender-returns-section">
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Report Book Damage
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Workflow: (1) Borrower returns the book → status becomes returned/delivered.
            (2) Within 24 hours, open Report Damage, add notes and photos (max 5).
            (3) Submit — the report is saved on the order for platform review and deposit handling.
          </p>
          {ownerOrders.filter((o) =>
            ['returned', 'delivered'].includes((o.status || '').toLowerCase())
            || o.pickup_status === 'collected'
          ).length === 0 ? (
            <p className="text-sm text-gray-400 border rounded-lg p-4 bg-gray-50">
              No returned books right now. When a borrower returns a book, it will appear here so you can file a damage report with photos.
            </p>
          ) : (
            <div className="space-y-3">
              {ownerOrders
                .filter((o) =>
                  ['returned', 'delivered'].includes((o.status || '').toLowerCase())
                  || o.pickup_status === 'collected'
                )
                .slice(0, 10)
                .map((order) => (
                  <div key={order.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-lg p-3">
                    <div>
                      <p className="font-medium">Order #{order.id?.slice(0, 8)}</p>
                      <p className="text-sm text-gray-600">
                        {(order.owned_items || order.items || []).map((i) => i.book_title || i.title).filter(Boolean).join(', ') || 'Book(s)'}
                      </p>
                      <p className="text-xs text-gray-400">Status: {order.status}{order.damage_report ? ' · Report filed' : ''}</p>
                    </div>
                    {!order.damage_report ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-300 text-amber-800 bg-amber-50"
                        onClick={() => { setDamageOrder(order); setDamageNotes(''); setDamagePhotos([]); }}
                        data-testid={`report-damage-${order.id}`}
                      >
                        Report Damage
                      </Button>
                    ) : (
                      <span className="text-xs text-green-700 font-medium">Report submitted</span>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Transaction history — MBR-120 */}
        {ownerOrders.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8" data-testid="lender-transaction-history">
            <h3 className="text-xl font-bold mb-4">Transaction History</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {ownerOrders
                .filter((o) => o.payment_status === 'completed')
                .slice(0, 20)
                .map((order) => (
                  <div key={order.id} className="flex justify-between items-center border rounded-lg p-3 text-sm">
                    <div>
                      <p className="font-medium">Rental — Order #{order.id?.slice(0, 8)}</p>
                      <p className="text-xs text-gray-500">
                        {(order.owned_items || order.items || []).map((i) => i.book_title || i.title).filter(Boolean).join(', ') || 'Book(s)'}
                      </p>
                      <p className="text-xs text-gray-400">{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</p>
                    </div>
                    <span className="font-bold text-green-700">+₹{Math.round(order.final_amount || 0)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <Dialog open={!!damageOrder} onOpenChange={(open) => { if (!open) setDamageOrder(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Report Damage</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-gray-600">
                Order #{damageOrder?.id?.slice(0, 8)} — include clear photos as evidence.
              </p>
              <Textarea
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                placeholder="Describe the damage..."
                rows={4}
                data-testid="damage-notes"
              />
              <div>
                <Label>Photos (max 5, 2MB each)</Label>
                <Input type="file" accept="image/*" multiple onChange={handleDamagePhotoChange} className="mt-1" data-testid="damage-photos" />
                {damagePhotos.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{damagePhotos.length} photo(s) selected</p>
                )}
              </div>
              <Button
                onClick={submitDamageReport}
                disabled={damageSubmitting}
                className="w-full"
                style={{backgroundColor: 'var(--primary)', color: 'white'}}
                data-testid="damage-submit"
              >
                {damageSubmitting ? 'Submitting…' : 'Submit Report'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Book Section */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">My Books</h3>
          
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              onClick={() => navigate('/bulk-book-upload')}
              className="flex items-center space-x-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="bulk-upload-button"
            >
              <FileSpreadsheet className="h-5 w-5" />
              <span>Bulk Upload</span>
            </Button>
            
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) {
                handleResetSearch();
                if (searchParams.get('add')) {
                  const next = new URLSearchParams(searchParams);
                  next.delete('add');
                  setSearchParams(next, { replace: true });
                }
              }
            }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
              <DialogHeader>
                <DialogTitle style={{color: 'var(--foreground)'}}>
                  {searchStep === 4
                    ? 'Your copy details'
                    : `Add New Book ${searchStep === 2 ? '- Select from Results' : searchStep === 3 ? '- Fill Details' : ''}`}
                </DialogTitle>
              </DialogHeader>
              
              {searchStep === 1 && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-sm" style={{color: 'var(--foreground)', opacity: 0.8}}>
                      Choose how you want to add a book:
                    </p>
                  </div>

                  {canAssignOwner && (
                    <div className="p-3 rounded-xl border border-blue-200 bg-blue-50/60">
                      <Label className="text-sm font-medium">Who owns this book?</Label>
                      <p className="text-xs text-gray-600 mb-2">
                        Employees and platform admins can list a book for a Prosumer. Pick the owner first, then use AI, Manual, or Bulk.
                      </p>
                      <Select value={selectedOwnerId || '__self__'} onValueChange={(v) => setSelectedOwnerId(v === '__self__' ? '' : v)}>
                        <SelectTrigger data-testid="owner-select-step1">
                          <SelectValue placeholder="Myself" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__self__">Myself ({user?.name})</SelectItem>
                          {prosumerOwners.filter(o => o.id !== user?.id).map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.name} ({o.email || o.phone || o.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* AI Search Option */}
                  <div className="p-4 border rounded-xl" style={{borderColor: 'var(--primary)', backgroundColor: 'rgba(139, 90, 43, 0.05)'}}>
                    <div className="flex items-center mb-3">
                      <Sparkles className="h-5 w-5 mr-2" style={{color: 'var(--primary)'}} />
                      <h4 className="font-semibold" style={{color: 'var(--foreground)'}}>Search with AI</h4>
                    </div>
                    <p className="text-xs mb-3" style={{color: 'var(--foreground)', opacity: 0.7}}>
                      Title-only search (use * as a wildcard). We check our catalogue first; if the title is not listed, we fetch matching titles from Google Books and Open Library.
                    </p>
                    <div className="flex space-x-2">
                      <Input
                        value={searchTitle}
                        onChange={(e) => setSearchTitle(e.target.value)}
                        placeholder="Enter book title"
                        data-testid="search-title-input"
                        className="flex-1 text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchBooks()}
                      />
                      <Button
                        type="button"
                        onClick={handleSearchBooks}
                        disabled={aiLoading}
                        style={{backgroundColor: 'var(--primary)', color: 'white'}}
                        className="hover:opacity-90"
                        data-testid="search-books-button"
                      >
                        {aiLoading ? 'Searching...' : 'Search'}
                      </Button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center">
                    <div className="flex-1 border-t" style={{borderColor: 'var(--border)'}}></div>
                    <span className="px-4 text-sm" style={{color: 'var(--foreground)', opacity: 0.5}}>OR</span>
                    <div className="flex-1 border-t" style={{borderColor: 'var(--border)'}}></div>
                  </div>

                  {/* Manual Entry Option */}
                  <div className="p-4 border rounded-xl" style={{borderColor: 'var(--accent)', backgroundColor: 'rgba(180, 83, 9, 0.05)'}}>
                    <div className="flex items-center mb-3">
                      <PenLine className="h-5 w-5 mr-2" style={{color: 'var(--accent)'}} />
                      <h4 className="font-semibold" style={{color: 'var(--foreground)'}}>Add Manually</h4>
                    </div>
                    <p className="text-xs mb-3" style={{color: 'var(--foreground)', opacity: 0.7}}>
                      Enter all book details yourself
                    </p>
                    <Button
                      type="button"
                      onClick={() => setSearchStep(3)}
                      variant="outline"
                      className="w-full"
                      style={{borderColor: 'var(--accent)', color: 'var(--accent)'}}
                      data-testid="manual-entry-button"
                    >
                      <PenLine className="h-4 w-4 mr-2" />
                      Enter Details Manually
                    </Button>
                  </div>

                  <div className="p-4 border rounded-xl" style={{borderColor: '#7c3aed', backgroundColor: 'rgba(124, 58, 237, 0.05)'}}>
                    <div className="flex items-center mb-3">
                      <FileSpreadsheet className="h-5 w-5 mr-2 text-purple-700" />
                      <h4 className="font-semibold" style={{color: 'var(--foreground)'}}>Bulk add</h4>
                    </div>
                    <p className="text-xs mb-3" style={{color: 'var(--foreground)', opacity: 0.7}}>
                      Paste or upload many titles. Enrich fills description, author, pages and cover — same fields as AI search.
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate('/bulk-book-upload')}
                      variant="outline"
                      className="w-full border-purple-300 text-purple-700"
                      data-testid="bulk-entry-button"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Open bulk sheet
                    </Button>
                  </div>
                </div>
              )}

              {searchStep === 2 && (
                <div className="space-y-4">
                  {searchResults.length === 0 ? (
                    <p className="text-center text-gray-500">No books found. Try a different title.</p>
                  ) : (
                    <>
                      {searchMessage && (
                        <p className="text-sm rounded-lg p-3" style={{backgroundColor: '#E8E4C9', color: 'var(--primary)'}}>
                          {searchMessage}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">Found {searchResults.length} result(s). Select a book:</p>
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                        {searchResults.map((book, index) => (
                          <div
                            key={index}
                            onClick={() => handleSelectBook(book)}
                            className="p-3 border rounded-lg cursor-pointer hover:shadow-md hover:border-amber-400 transition-all flex gap-3"
                            style={{backgroundColor: 'var(--card)'}}
                            data-testid={`search-result-${index}`}
                          >
                            {bookCoverUrl(book) ? (
                              <img
                                src={bookCoverUrl(book)}
                                alt={book.title}
                                className="w-14 h-20 object-cover rounded flex-shrink-0 bg-gray-100"
                                onError={(e) => {
                                  const isbn = String(book.isbn_13 || book.isbn_10 || book.isbn || '').replace(/\D/g, '');
                                  const fallback = isbn ? `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg` : '';
                                  if (fallback && e.target.dataset.fallback !== '1') {
                                    e.target.dataset.fallback = '1';
                                    e.target.src = fallback;
                                    return;
                                  }
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-14 h-20 rounded flex-shrink-0 flex items-center justify-center" style={{backgroundColor: '#f3ece0'}}>
                                <BookOpen className="h-6 w-6" style={{color: 'var(--primary)'}} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="font-bold text-sm leading-tight" style={{color: 'var(--foreground)'}}>{book.title}</h4>
                                {book.source === 'internal' && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-800 flex-shrink-0">
                                    On platform
                                  </span>
                                )}
                              </div>
                              <p className="text-xs mt-0.5" style={{color: 'var(--foreground)', opacity: 0.8}}>by {book.author}</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {book.publisher && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{book.publisher}</span>}
                                {book.pages && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{book.pages} pages</span>}
                                {book.language && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{book.language}</span>}
                                {book.year && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{book.year}</span>}
                                {book.category && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{book.category}</span>}
                                {book.current_stock != null && book.source === 'internal' && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Stock {book.current_stock}</span>
                                )}
                              </div>
                              {book.description && (
                                <p className="text-xs mt-1.5 line-clamp-2 text-gray-500">{book.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <Button
                    type="button"
                    onClick={handleResetSearch}
                    variant="outline"
                    className="w-full"
                  >
                    Search Again
                  </Button>
                </div>
              )}

              {searchStep === 4 && existingPick && (
                <form onSubmit={handleAddExistingCopy} className="space-y-4" data-testid="copy-details-form">
                  <div className="flex gap-3 p-3 rounded-lg border" style={{backgroundColor: 'var(--card)'}}>
                    {bookCoverUrl(existingPick) ? (
                      <img src={bookCoverUrl(existingPick)} alt="" className="w-14 h-20 object-cover rounded bg-gray-100" />
                    ) : (
                      <div className="w-14 h-20 rounded flex items-center justify-center" style={{backgroundColor: '#f3ece0'}}>
                        <BookOpen className="h-6 w-6" style={{color: 'var(--primary)'}} />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{existingPick.title}</p>
                      <p className="text-sm text-gray-600">by {existingPick.author}</p>
                      <p className="text-xs text-green-700 mt-1">Already on MyBook.Rent — we'll add your copy only.</p>
                    </div>
                  </div>

                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={copyForm.useProfileAddress}
                      onCheckedChange={(checked) => setCopyForm((prev) => ({ ...prev, useProfileAddress: Boolean(checked) }))}
                      data-testid="copy-use-profile-address"
                    />
                    <span>Use address from my profile</span>
                  </label>

                  {copyForm.useProfileAddress ? (
                    <div className="text-sm rounded-md border px-3 py-2 bg-gray-50" data-testid="copy-profile-address">
                      <p>{user?.address || 'No street address on profile'}</p>
                      <p>{[user?.city, user?.pincode].filter(Boolean).join(' — ') || 'Add city and pincode in Edit Profile'}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={copyForm.address}
                          onChange={(e) => setCopyForm({ ...copyForm, address: e.target.value })}
                          placeholder="House / street"
                          data-testid="copy-address-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>City *</Label>
                          <Input
                            value={copyForm.city}
                            onChange={(e) => setCopyForm({ ...copyForm, city: e.target.value })}
                            placeholder="City"
                            data-testid="copy-city-input"
                          />
                        </div>
                        <div>
                          <Label>Pincode *</Label>
                          <Input
                            value={copyForm.pincode}
                            onChange={(e) => setCopyForm({ ...copyForm, pincode: e.target.value })}
                            placeholder="Pincode"
                            data-testid="copy-pincode-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Your MRP (₹) *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={copyForm.mrp}
                      onChange={(e) => setCopyForm({ ...copyForm, mrp: e.target.value })}
                      placeholder="e.g. 399"
                      required
                      data-testid="copy-mrp-input"
                    />
                    {copyForm.mrp && parseFloat(copyForm.mrp) > 0 && (
                      <p className="text-xs mt-1" style={{color: 'var(--accent)'}} data-testid="copy-daily-rate">
                        Daily rate: ₹{dailyRateFromMrp(copyForm.mrp)} / day (30% of MRP per week)
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>Book condition *</Label>
                    <Select
                      value={copyForm.condition}
                      onValueChange={(value) => setCopyForm({ ...copyForm, condition: value })}
                    >
                      <SelectTrigger data-testid="copy-condition-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex space-x-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setSearchStep(2)}>
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={copySubmitting}
                      className="flex-1 hover:opacity-90"
                      style={{backgroundColor: 'var(--primary)', color: 'white'}}
                      data-testid="copy-details-submit"
                    >
                      {copySubmitting ? 'Saving...' : 'Add my copy'}
                    </Button>
                  </div>
                </form>
              )}

              {searchStep === 3 && (
                <form onSubmit={handleAddBook} className="space-y-4">
                  {aiLoading && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="enrich-loading">
                      Filling price, category, weight, size, reading age and pages…
                    </p>
                  )}
                  {canAssignOwner && (
                    <div>
                      <Label>List on behalf of Prosumer (optional)</Label>
                      <Select value={selectedOwnerId || '__self__'} onValueChange={(v) => setSelectedOwnerId(v === '__self__' ? '' : v)}>
                        <SelectTrigger data-testid="owner-select">
                          <SelectValue placeholder="Myself" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__self__">Myself ({user?.name})</SelectItem>
                          {prosumerOwners.filter(o => o.id !== user?.id).map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.name} ({o.email || o.phone || o.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Title *</Label>
                      <Input
                        value={bookForm.title}
                        onChange={(e) => setBookForm({...bookForm, title: e.target.value})}
                        placeholder="Enter book title"
                        required
                        data-testid="book-title-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Author *</Label>
                      <Input
                        value={bookForm.author}
                        onChange={(e) => setBookForm({...bookForm, author: e.target.value})}
                        placeholder="Enter author name"
                        required
                        data-testid="book-author-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                  
                    <div>
                      <Label>Category *</Label>
                      <Select 
                        value={bookForm.category} 
                        onValueChange={(value) => setBookForm({...bookForm, category: value, subcategory: ''})}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Subcategory dropdown for Language category */}
                    {hasSubcategories && (
                      <div>
                        <Label>Language *</Label>
                        <Select 
                          value={bookForm.subcategory} 
                          onValueChange={(value) => setBookForm({...bookForm, subcategory: value})}
                        >
                          <SelectTrigger data-testid="subcategory-select">
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedCategory.subcategories.map((sub, idx) => (
                              <SelectItem key={idx} value={sub}>{sub}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div>
                      <Label>Price (₹) * <span className="text-xs font-normal text-gray-500">(Rental = 30% of MRP)</span></Label>
                      <Input
                        type="number"
                        value={bookForm.price}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setBookForm({...bookForm, price: value.toString()});
                        }}
                        placeholder="MRP (whole numbers only)"
                        required
                        data-testid="book-price-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                      {bookForm.price && (
                        <p className="text-xs mt-1" style={{color: 'var(--accent)'}}>
                          Rental Price: ₹{Math.round(parseInt(bookForm.price) * 0.30)}/week
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label>Stock</Label>
                      <Input
                        type="number"
                        value={bookForm.stock}
                        onChange={(e) => setBookForm({...bookForm, stock: e.target.value})}
                        min="1"
                        data-testid="book-stock-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div>
                      <Label>Pincode *</Label>
                      <Input
                        value={bookForm.pincode}
                        onChange={(e) => setBookForm({...bookForm, pincode: e.target.value})}
                        placeholder="Location pincode"
                        required
                        data-testid="book-pincode-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Description</Label>
                      <textarea
                        className="w-full p-2 border rounded-md text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                        rows="3"
                        value={bookForm.description}
                        onChange={(e) => setBookForm({...bookForm, description: e.target.value})}
                        placeholder="Book description"
                        data-testid="book-description-input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Cover Image URL (or upload manually)</Label>
                      <Input
                        value={bookForm.cover_image}
                        onChange={(e) => setBookForm({...bookForm, cover_image: e.target.value})}
                        placeholder="Paste image URL or upload below"
                        data-testid="book-cover-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                      <p className="text-xs text-gray-500 mt-1">Max allowed size: 3 MB. Recommended: 600 x 900 px (portrait, 2:3 ratio). Formats: JPEG, PNG.</p>
                    </div>
                    
                    <div>
                      <Label>Weight (gms)</Label>
                      <Input
                        type="number"
                        value={bookForm.weight_gms}
                        onChange={(e) => setBookForm({...bookForm, weight_gms: e.target.value})}
                        placeholder="e.g., 300"
                        data-testid="book-weight-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div>
                      <Label>Size (cms)</Label>
                      <Input
                        value={bookForm.size_cms}
                        onChange={(e) => setBookForm({...bookForm, size_cms: e.target.value})}
                        placeholder="e.g., 20x15x2"
                        data-testid="book-size-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div>
                      <Label>Reading Age</Label>
                      <Select
                        value={bookForm.reading_age || undefined}
                        onValueChange={(value) => setBookForm({...bookForm, reading_age: value})}
                      >
                        <SelectTrigger data-testid="book-reading-age-input">
                          <SelectValue placeholder="Select age" />
                        </SelectTrigger>
                        <SelectContent>
                          {READING_AGES.map((age) => (
                            <SelectItem key={age} value={age}>{age} years</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Publisher</Label>
                      <Input
                        value={bookForm.publisher}
                        onChange={(e) => setBookForm({...bookForm, publisher: e.target.value})}
                        data-testid="book-publisher-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div>
                      <Label>Pages</Label>
                      <Input
                        type="number"
                        value={bookForm.pages}
                        onChange={(e) => setBookForm({...bookForm, pages: e.target.value})}
                        data-testid="book-pages-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div>
                      <Label>Language</Label>
                      <Input
                        value={bookForm.language}
                        onChange={(e) => setBookForm({...bookForm, language: e.target.value})}
                        data-testid="book-language-input"
                        className="text-foreground placeholder:text-[hsl(20,15%,35%)]"
                        style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                      />
                    </div>
                    
                    <div className="col-span-2 flex space-x-2">
                      <Button type="button" onClick={handleResetSearch} variant="outline" className="flex-1">
                        Search Again
                      </Button>
                      <Button type="submit" disabled={aiLoading} style={{backgroundColor: 'var(--primary)', color: 'white'}} className="flex-1 hover:opacity-90" data-testid="submit-book-button">
                        {aiLoading ? 'Filling details...' : 'Add Book'}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Books Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton h-80 rounded-2xl"></div>
            ))}
          </div>
        ) : myBooks.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <BookOpen className="h-20 w-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-600 mb-2" data-testid="no-books-message">No books yet</h3>
            <p className="text-gray-500 mb-6">Start by adding your first book</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {myBooks.map((book) => (
              <div key={book.id} className="bg-white rounded-2xl shadow-md overflow-hidden" data-testid={`admin-book-card-${book.id}`}>
                <div className="h-48 flex items-center justify-center" style={{backgroundColor: 'var(--card)'}}>
                  {book.cover_image ? (
                    <img src={book.cover_image} alt={book.title} className="h-full w-full object-cover" />
                  ) : (
                    <BookOpen className="h-16 w-16" style={{color: 'var(--primary)'}} />
                  )}
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-lg text-gray-900 mb-1">{book.title}</h4>
                  <p className="text-sm text-gray-600 mb-1">{book.author}</p>
                  {isPlatformAdmin(user?.role) && book.owner_name && (
                    <p className="text-xs text-gray-500 mb-3">Owner: {book.owner_name}</p>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    {book._source === 'two_layer' ? (
                      <span className="text-xl font-bold" style={{color: 'var(--accent)'}}>₹{Math.round(book.price)}/day</span>
                    ) : (
                      <span className="text-xl font-bold" style={{color: 'var(--accent)'}}>₹{Math.round(book.rental_price_per_week)}/week</span>
                    )}
                    {(book._source === 'two_layer' ? book.status === 'available' : book.is_available) ? (
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                        Available
                      </span>
                    ) : (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                        Rented
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => book._source === 'two_layer' ? navigate(`/catalog/${book.book_id}`) : navigate(`/books/${book.id}`)}
                      data-testid={`view-book-button-${book.id}`}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => book._source === 'two_layer' ? navigate('/my-copies') : handleEditBook(book)}
                      data-testid={`edit-book-button-${book.id}`}
                      style={{borderColor: 'var(--accent)', color: 'var(--accent)'}}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteBook(book)}
                      data-testid={`delete-book-button-${book.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Book Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          setEditingBook(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{backgroundColor: 'var(--background)', color: 'var(--foreground)'}}>
          <DialogHeader>
            <DialogTitle style={{color: 'var(--foreground)'}}>Edit Book - {editingBook?.title}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateBook} className="space-y-4">
            {editDuplicateMatch && (
              <div
                className="rounded-lg border-2 border-red-500 bg-red-50 p-4 text-red-800"
                data-testid="edit-duplicate-warning"
              >
                <p className="font-bold text-base flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Duplicate book detected
                </p>
                <p className="text-sm mt-1">
                  Another listing matches &ldquo;{editDuplicateMatch.title}&rdquo; by {editDuplicateMatch.author}.
                  Consider updating stock on the existing record instead of creating a duplicate.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input
                  value={bookForm.title}
                  onChange={(e) => setBookForm({...bookForm, title: e.target.value})}
                  placeholder="Enter book title"
                  required
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Author *</Label>
                <Input
                  value={bookForm.author}
                  onChange={(e) => setBookForm({...bookForm, author: e.target.value})}
                  placeholder="Enter author name"
                  required
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select 
                  value={bookForm.category} 
                  onValueChange={(value) => setBookForm({...bookForm, category: value, subcategory: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {hasSubcategories && (
                <div>
                  <Label>Language *</Label>
                  <Select 
                    value={bookForm.subcategory} 
                    onValueChange={(value) => setBookForm({...bookForm, subcategory: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCategory.subcategories.map((sub, idx) => (
                        <SelectItem key={idx} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Price (₹) * <span className="text-xs font-normal text-gray-500">(Rental = 30% of MRP)</span></Label>
                <Input
                  type="number"
                  value={bookForm.price}
                  onChange={(e) => setBookForm({...bookForm, price: e.target.value})}
                  placeholder="MRP"
                  required
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
                {bookForm.price && (
                  <p className="text-xs mt-1" style={{color: 'var(--accent)'}}>
                    Rental Price: ₹{Math.round(parseInt(bookForm.price) * 0.30)}/week
                  </p>
                )}
              </div>
              
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={bookForm.stock}
                  onChange={(e) => setBookForm({...bookForm, stock: e.target.value})}
                  min="1"
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Pincode *</Label>
                <Input
                  value={bookForm.pincode}
                  onChange={(e) => setBookForm({...bookForm, pincode: e.target.value})}
                  placeholder="Location pincode"
                  required
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <textarea
                  className="w-full p-2 border rounded-md text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                  rows={3}
                  value={bookForm.description}
                  onChange={(e) => setBookForm({...bookForm, description: e.target.value})}
                  placeholder="Book description..."
                />
              </div>
              
              <div className="col-span-2">
                <Label>Cover Image URL</Label>
                <Input
                  value={bookForm.cover_image}
                  onChange={(e) => setBookForm({...bookForm, cover_image: e.target.value})}
                  placeholder="https://..."
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Publisher</Label>
                <Input
                  value={bookForm.publisher}
                  onChange={(e) => setBookForm({...bookForm, publisher: e.target.value})}
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Pages</Label>
                <Input
                  type="number"
                  value={bookForm.pages}
                  onChange={(e) => setBookForm({...bookForm, pages: e.target.value})}
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Language</Label>
                <Input
                  value={bookForm.language}
                  onChange={(e) => setBookForm({...bookForm, language: e.target.value})}
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div>
                <Label>Weight (gms)</Label>
                <Input
                  type="number"
                  value={bookForm.weight_gms}
                  onChange={(e) => setBookForm({...bookForm, weight_gms: e.target.value})}
                  className="text-foreground"
                  style={{color: 'var(--foreground)', backgroundColor: 'white'}}
                />
              </div>
              
              <div className="col-span-2 flex space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  style={{backgroundColor: 'var(--primary)', color: 'white'}} 
                  className="flex-1 hover:opacity-90"
                >
                  Update Book
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
